import React from 'react';
// Toaster removed - using global Toaster from App.js
import { useLiveSession } from '../hooks/useLiveSession/useLiveSession';
import logger from '../utils/logger';
import { LoadingState, ErrorState } from './LiveSession/LoadingStates';
import { LiveSessionSkeleton } from './LiveSession/LiveSessionSkeleton';
import { LiveSessionBanner } from './LiveSession/LiveSessionBanner';
import { LiveSessionDetailsPanel } from './LiveSession/LiveSessionDetailsPanel';
import { LiveSessionActionsPanel } from './LiveSession/LiveSessionActionsPanel';
import { AssessmentQuestionsArea } from './LiveSession/AssessmentQuestionsArea';
import { SessionSummaryModal } from './LiveSession/SessionSummaryModal';

const LiveSessionInterface = ({ sessionId, isConsultant = false }) => {
  // Use the main live session hook
  const {
    session,
    loading,
    error,
    endingSession,
    completingSession,
    sessionSummary,
    showSummary,
    currentAnswers,
    savingAnswers,
    currentQuestionIndex,
    handleEndSession,
    handleCancelSession,
    handleCompleteSession,
    handleLeaveSession,
    handleAnswerChange,
    submitAnswer,
    goToNextQuestion,
    goToPreviousQuestion,
    setShowSummary,
    fetchSession
  } = useLiveSession({ sessionId, isConsultant });

  // Show loading state while loading
  if (loading) {
    return <LiveSessionSkeleton />;
  }

  // Show error state if there's an error
  if (error) {
    return <ErrorState error={error} isConsultant={isConsultant} />;
  }

  // Final safety check - ensure session exists, is an object, and has required properties
  // This prevents "Cannot read properties of null" errors during React Strict Mode double renders
  if (!session || 
      typeof session !== 'object' || 
      !session.id || 
      typeof session.status === 'undefined') {
    // Session is not fully loaded yet
    return <LiveSessionSkeleton />;
  }

  // Safely extract otherPerson with null checks
  const otherPerson = isConsultant ? (session?.user || null) : (session?.consultant || null);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Toaster removed - using global Toaster from App.js */}
      
      {/* Top Banner */}
      <LiveSessionBanner 
        session={session} 
        isConsultant={isConsultant} 
        otherPerson={otherPerson} 
      />

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Left Panel - Session Details */}
          <div className="lg:col-span-1 space-y-6">
            <LiveSessionDetailsPanel 
              session={session} 
              isConsultant={isConsultant} 
              otherPerson={otherPerson} 
            />
            
            <LiveSessionActionsPanel
              session={session}
              isConsultant={isConsultant}
              endingSession={endingSession}
              showSummary={showSummary}
              onEndSession={handleEndSession}
              onCancelSession={handleCancelSession}
              onCompleteSession={() => {
                // Ensure session exists before calling
                if (session) {
                  handleCompleteSession(!isConsultant);
                }
              }}
              onLeaveSession={handleLeaveSession}
              onToggleSummary={() => setShowSummary(!showSummary)}
            />
          </div>

          {/* Right Panel - Main Assessment Area */}
          <AssessmentQuestionsArea
            session={session}
            currentQuestionIndex={currentQuestionIndex}
            currentAnswers={currentAnswers}
            savingAnswers={savingAnswers}
            isConsultant={isConsultant}
            completingSession={completingSession}
            onAnswerChange={handleAnswerChange}
            onNextQuestion={goToNextQuestion}
            onPreviousQuestion={goToPreviousQuestion}
            onSubmit={() => {
              // Ensure session exists before calling
              if (session) {
                handleCompleteSession(!isConsultant);
              }
            }}
            fetchSession={fetchSession}
          />
        </div>
      </div>

      {/* Session Summary Modal */}
      {showSummary && sessionSummary && (
        <SessionSummaryModal 
          sessionSummary={sessionSummary} 
          onClose={() => setShowSummary(false)} 
        />
      )}
    </div>
  );
};

export default LiveSessionInterface;
